// app/src/components/SignUpPageMobilePartPanel1.tsx

import React, { useEffect, useMemo, useState, FC } from 'react';
import { useTranslation } from "react-i18next";
import { UserType } from '../../../types';
import CompaniesService from '../../../services/Companies/CompaniesService';
import formatNBTVA from '../../../utils/functions/formatNBTVA';
import formatSireXNumber from '../../../utils/functions/formatSireXNumber';
import removeSpaces from '../../../utils/functions/removeSpaces';
import { useAuthContext } from "../../../contexts/AuthContext";
import TextInputMobile from "../atomicDesignMobile/atoms/TextInputMobile";
import SelectInputMobile from "../atomicDesignMobile/atoms/SelectInputMobile";
import { Checkbox } from "@mui/material";
import toTitleCase from "../../../utils/functions/toTitleCase";
import formatPhoneNumber from "../../../utils/functions/formatPhoneNumber";
import VPAutoService from "../../../services/VPAuto/VPAutoService";
import { emitAnalytics, getTelemetryDeviceType } from '../../../services/telemetry/appInsights';

interface SignUpPageMobilePartPanel1Props {
	isOpen: boolean;
	setIsOpen: (open: boolean) => void;
}

const SignUpPageMobilePartPanel1: FC<SignUpPageMobilePartPanel1Props> = ({
	isOpen,
	setIsOpen,
	}) => {
		const {
			userInfo,
			setUserInfo,
			isFirstPanelValidated,
			loadingSignup,
			handleSignUpPart,
		} = useAuthContext();
		const { t, i18n } = useTranslation();
	const [isTooltipVisible, setIsTooltipVisible] = useState(false);
	const [fetchingCompanyData, setFetchingCompanyData] = useState(false);
	const [disableFields, setDisableFields] = useState({
		nbtva: false,
		companyName: false,
	});
	const [optionsListHowDidYouKnowUs, setOptionsListHowDidYouKnowUs] = useState<{
		value: string,
		label: string
	}[]>([]);

	const [wrongFields, setWrongFields] = useState({
		profile: false,
		email: false,
		companyName: false,
		lastName: false,
		firstName: false,
		phone: false,
		siret: false,
		nbtva: false,
	});

	useEffect(() => {
		VPAutoService.readOptionsListHowDidYouKnowUs(UserType.PART)
			.then((response) => {
				const optionsList = response.data.value.split(';').map((value: string) => ({
					value,
					label: value
				}));
				setOptionsListHowDidYouKnowUs([
					{ value: '', label: '' },
					...optionsList,
				]);
			}).catch(() => {});
	}, []);

	useEffect(() => {
		const fetchCompanyData = async (sirenOrSiret: string, isSiret: boolean) => {
			setFetchingCompanyData(true);
			emitAnalytics('part_signup_company_lookup_started', {
				entryRoute: '/part/signup',
				countryCode: userInfo.company.countryCode,
				identifierType: 'vat',
				lookupMode: 'classic',
				deviceType: getTelemetryDeviceType(),
				language: i18n.language,
			});
			try {
				const { data } = await CompaniesService.sirenInformationRead(isSiret ? sirenOrSiret.substring(0, 9) : sirenOrSiret);
				if (data.denominationUniteLegale === null || data.denominationUniteLegale.includes("[ND] [ND]")) {
					emitAnalytics('part_signup_company_lookup_result', {
						entryRoute: '/part/signup',
						countryCode: userInfo.company.countryCode,
						identifierType: 'vat',
						lookupMode: 'classic',
						lookupOutcome: 'no_match',
						deviceType: getTelemetryDeviceType(),
						language: i18n.language,
					});
					setDisableFields({
						nbtva: false,
						companyName: false,
					});
				} else {
					emitAnalytics('part_signup_company_lookup_result', {
						entryRoute: '/part/signup',
						countryCode: userInfo.company.countryCode,
						identifierType: 'vat',
						lookupMode: 'classic',
						lookupOutcome: 'match',
						deviceType: getTelemetryDeviceType(),
						language: i18n.language,
					});
					setDisableFields({
						nbtva: true,
						companyName: true,
					});
					setUserInfo((prevState: any) => ({
						...prevState,
						company: {
							...prevState.company,
							siret: data.siret || prevState.company.siret,
							siren: data.siren || prevState.company.siren,
							companyName: toTitleCase(data.denominationUniteLegale) || prevState.company.companyName,
							nbtva: data.tva || prevState.company.nbtva,
						},
					}));
				}
			}
			catch (error) {
				emitAnalytics('part_signup_company_lookup_result', {
					entryRoute: '/part/signup',
					countryCode: userInfo.company.countryCode,
					identifierType: 'vat',
					lookupMode: 'classic',
					lookupOutcome: 'error',
					deviceType: getTelemetryDeviceType(),
					language: i18n.language,
				});
				console.error('Erreur lors de la récupération des informations de l\'entreprise', error);
			}
			finally {
				setFetchingCompanyData(false);
			}
		};

		if (removeSpaces(userInfo.company.siret).length === 14) {
			fetchCompanyData(userInfo.company.siret, true);
		}
	}, [userInfo.company.siret, userInfo.company.siren, setUserInfo]);

	const validateFields = () => {
		const newWrongFields = {
			profile: !userInfo.contact.profile,
			email: !userInfo.contact.email,
			lastName: !userInfo.contact.lastName,
			firstName: !userInfo.contact.firstName,
			companyName: userInfo.contact.profile === 'partCompany' ? !userInfo.company.companyName : false,
			phone: !userInfo.contact.phone,
			siret :  userInfo.contact.profile === 'partCompany' ? !userInfo.company.siret : false,
			nbtva : userInfo.contact.profile === 'partCompany' ? !userInfo.company.nbtva : false
		};
		setWrongFields(newWrongFields);
		return Object.values(newWrongFields).every((value) => !value);
	};

	const blockNbtva = useMemo(() => {
		return (
			<TextInputMobile
				width={260}
				type="text"
				value={formatNBTVA(userInfo.company.nbtva)}
				placeholder="FR 12 123456789"
				title={t('secondPanel:vat_number_mobile')}
				mandatory
				disabled={disableFields.nbtva}
				loading={fetchingCompanyData}
				onChange={(e) =>
					setUserInfo((prevState: any) => ({
						...prevState,
						company: {
							...prevState.company,
							nbtva: removeSpaces(e.target.value).toUpperCase(),
						},
					}))
				}
				wrong={wrongFields.nbtva}
				wrongMessage={t('secondPanel:fill_this_field')}
			/>
		);
	}, [userInfo.company.nbtva, userInfo.company.countryCode, fetchingCompanyData, wrongFields.nbtva, disableFields.nbtva, t, setUserInfo]);

	const blockSiret = useMemo(() => {
		return (
			<TextInputMobile
				width={260}
				type="text"
				value={formatSireXNumber(userInfo.company.siret)}
				placeholder="123 456 789 00001"
				title={t('secondPanel:siret')}
				mandatory
				loading={fetchingCompanyData}
				onChange={(e) => {
					const newValue = removeSpaces(e.target.value);
					if (/^\d*$/.test(newValue)) {
						setUserInfo((prevState: any) => ({
							...prevState,
							company: {
								...prevState.company,
								siret: newValue,
								siren: newValue.length === 14 ? newValue.substring(0, 9) : '',
							},
						}));
					}
				}}
				wrong={wrongFields.siret}
				wrongMessage={t('secondPanel:wrong_siret')}
			/>
		);
	}, [userInfo.company.siret, fetchingCompanyData, wrongFields.siret, t, setUserInfo]);

	const blockReceiverRecommendations = useMemo(() => {
		return (
			<div
				style={{
					width: 260,
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'start',
					justifyContent: 'space-between',
					position: 'relative',
					overflow: 'visible',
					gap: 10,
				}}
			>
				<div
					style={{
						display: 'flex',
						flexDirection: 'row',
						alignItems: 'center',
					}}
				>
					<div style={{ fontSize: 13 }}>
						{t('secondPanel:receive_recommendations')}
					</div>
					<div
						onMouseEnter={() => setIsTooltipVisible(true)}
						onMouseLeave={() => setIsTooltipVisible(false)}
						style={{
							position: 'relative',
						}}
					>
						<img
							src="/images/info.svg"
							alt="info"
							style={{
								width: 12,
								height: 12,
								marginLeft: 5,
								marginTop: -8,
								cursor: 'pointer',
							}}
						/>
						{isTooltipVisible && (
							<div
								style={{
									position: 'absolute',
									left: '20px',
									top: '-10px',
									backgroundColor: '#F6F8FB',
									padding: '5px 10px',
									borderRadius: '5px',
									boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
									zIndex: 10,
								}}
							>
								<div>{t('secondPanel:tooltip_info:latest_news')}</div>
							</div>
						)}
					</div>
				</div>

				<div
					style={{
						display: 'flex',
						flexDirection: 'row',
						alignItems: 'center',
						gap: 10,
					}}
				>
					<Checkbox
						checked={userInfo.receiveRecommendation.email}
						onChange={(e) =>
							setUserInfo((prevState: any) => ({
								...prevState,
								receiveRecommendation: {
									...prevState.receiveRecommendation,
									email: e.target.checked,
								},
							}))
						}
						sx={{ padding: 0 }}
						size={'small'}
					/>
					<div
						style={{ fontSize: 12 }}>{t('secondPanel:email')}</div>
				</div>
				<div
					style={{
						display: 'flex',
						flexDirection: 'row',
						alignItems: 'center',
						gap: 10,
					}}
				>
					<Checkbox
						checked={userInfo.receiveRecommendation.sms}
						onChange={(e) =>
							setUserInfo((prevState: any) => ({
								...prevState,
								receiveRecommendation: {
									...prevState.receiveRecommendation,
									sms: e.target.checked,
								},
							}))
						}
						sx={{ padding: 0 }}
						size={'small'}
					/>
					<div
						style={{ fontSize: 12 }}>{t('secondPanel:sms')}</div>
				</div>
			</div>
		)
	}, [userInfo.receiveRecommendation, isTooltipVisible, t, setUserInfo]);

	return (
		<div
			style={{
				marginTop: 18,
				width: 277,
				height: isOpen ? 'auto' : 20,
				background: '#FFFFFF',
				borderRadius: 10,
				position: 'relative',
				padding: 12,
				boxShadow: '0px 1px 6px #00000029',
				transition: 'height 0.3s ease',
			}}
		>
			{/* Header du panel */}
			<div
				style={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'space-between',
					alignItems: 'center',
					cursor: 'pointer',
				}}
				onClick={() => setIsOpen(!isOpen)}
			>
				<div style={{ height: 20, display: 'flex', flexDirection: 'row', gap: 8 }}>
					{isFirstPanelValidated ? (
						<img
							src="/images/check-2.svg"
							alt="check"
							style={{ width: 18, height: 16 }}
						/>
					) : (
						<div style={{ width: 18 }} />
					)}
					<div
						style={{
							fontSize: 16,
							fontWeight: 'bold',
							display: 'flex',
							alignItems: 'center',
						}}
					>
						{t('firstPanel:contact_info')}
					</div>
				</div>

				{isOpen ? (
					<img
						src="/images/chevron-down.svg"
						alt="chevron-down"
						style={{ width: 16, height: 16, marginRight: 5 }}
					/>
				) : (
					<img
						src="/images/chevron-right.svg"
						alt="chevron-right"
						style={{ width: 20, height: 20, marginRight: 5 }}
					/>
				)}
			</div>

			{isOpen && (
				<>
					<div
						style={{
							margin: '10px 10px 0 10px',
							display: 'flex',
							flexDirection: 'column',
							gap: 10,
						}}
					>
						<div style={{ height: 2 }}></div>
						<SelectInputMobile
							width={260}
							value={userInfo.contact.profile}
							placeholder={t('signupPanelPart:my_profile_placeholder')}
							options={[
								{ value: '', label: '' },
								{
									value: 'partIndividual',
									label: t('signupPanelPart:my_profile_options:noSociety')
								},
								{
									value: 'partCompany',
									label: t('signupPanelPart:my_profile_options:society')
								},
							]}
							title={t('signupPanelPart:my_profile_title')}
							mandatory
							onChange={(e) =>
								setUserInfo({
									...userInfo,
									contact: {
										...userInfo.contact,
										profile: e.target.value,
									},
								})
							}
							wrong={wrongFields.profile}
							wrongMessage={t('firstPanel:fill_this_field')}
						/>
						{userInfo.contact.profile === 'partCompany' && <div style={{ height: 2 }}></div>}
						{userInfo.contact.profile === 'partCompany' &&
							<>
								{blockSiret}
								{blockNbtva}
								<TextInputMobile
									width={260}
									type="text"
									value={userInfo.company.companyName}
									placeholder="Dupont Peinture"
									title={t('signupPanelPart:commercial_name')}
									mandatory
									onChange={(e) =>
										setUserInfo({
											...userInfo,
											company: {
												...userInfo.company,
												companyName: toTitleCase(e.target.value),
											},
										})
									}
									wrong={wrongFields.companyName}
									wrongMessage={t('firstPanel:fill_this_field')}
								/>
							</>
						}
						<div style={{ height: 2 }}></div>
						<TextInputMobile
							width={260}
							type="text"
							value={userInfo.contact.firstName}
							placeholder="Jean"
							title={t('firstPanel:first_name')}
							mandatory
							onChange={(e) =>
								setUserInfo({
									...userInfo,
									contact: {
										...userInfo.contact,
										firstName: toTitleCase(e.target.value),
									},
								})
							}
							wrong={wrongFields.firstName}
							wrongMessage={t('firstPanel:fill_this_field')}
						/>
						<div style={{ height: 2 }}></div>
						<TextInputMobile
							width={260}
							type="text"
							value={userInfo.contact.lastName}
							placeholder="Dupont"
							title={t('firstPanel:last_name')}
							mandatory
							onChange={(e) =>
								setUserInfo({
									...userInfo,
									contact: {
										...userInfo.contact,
										lastName: toTitleCase(e.target.value),
									},
								})
							}
							wrong={wrongFields.lastName}
							wrongMessage={t('firstPanel:fill_this_field')}
						/>
						<div style={{ height: 2 }}></div>
						<TextInputMobile
							width={260}
							type="text"
							value={formatPhoneNumber(userInfo.contact.phone)}
							placeholder="06 12 34 56 78"
							title={t('firstPanel:phone')}
							mandatory
							onChange={(e) =>
								setUserInfo({
									...userInfo,
									contact: {
										...userInfo.contact,
										phone: formatPhoneNumber(e.target.value),
									},
								})
							}
							wrong={wrongFields.phone}
							wrongMessage={t('firstPanel:fill_this_field')}
						/>
						<div style={{ height: 2 }}></div>
						<TextInputMobile
							width={260}
							type="text"
							value={userInfo.contact.email}
							title={t('firstPanel:email')}
							mandatory
							disabled
							onChange={() => {}}
							wrong={wrongFields.email}
							wrongMessage={t('firstPanel:fill_this_field')}
						/>
						<div style={{ height: 2 }}></div>
						<SelectInputMobile
							width={260}
							value={userInfo.contact.howDidYouKnowUs}
							options={optionsListHowDidYouKnowUs}
							title={t('firstPanel:how_did_you_know_us')}
							placeholder="LinkedIn"
							onChange={(e) =>
								setUserInfo({
									...userInfo,
									contact: {
										...userInfo.contact,
										howDidYouKnowUs: e.target.value,
									},
								})
							}
						/>
						<div style={{ height: 2 }}></div>
						{blockReceiverRecommendations}
						<div style={{
							width: '100%',
							textAlign: 'center',
							marginTop: 10
						}}>
								<button
									disabled={loadingSignup}
									style={{
									textAlign: 'center',
									height: 28,
									width: 121,
									borderRadius: 5,
									backgroundColor: '#FF4D00',
									color: '#FFFFFF',
									border: 'none',
									fontSize: 14,
									fontWeight: 'bold',
										cursor: loadingSignup ? 'not-allowed' : 'pointer',
										opacity: loadingSignup ? 0.7 : 1,
										textTransform: 'uppercase',
									}}
								onClick={() => {
								if (validateFields()) {
									emitAnalytics('part_signup_panel1_validated', {
										entryRoute: '/part/signup',
										deviceType: getTelemetryDeviceType(),
										language: i18n.language,
									});
									handleSignUpPart();
								}
							}}
							>
								{t('firstPanel:validate_button')}
							</button>
						</div>
					</div>
				</>
			)}
		</div>
	);
};

export default SignUpPageMobilePartPanel1;
